// src/pages/Membership.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuthContext } from "../context";
import { paymentURL, verify_paymentURL } from "../api/api";
import { Helmet } from "react-helmet";

const Membership = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);

  const scrollToTop = () =>
      window.scrollTo({ top: 0, behavior: "smooth" });

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleUpgrade = async () => {
    const token = localStorage.getItem("token");
    if (!token || !user) {
      toast.error("Please log in or sign up first.");
      navigate("/login");
      scrollToTop();  
      return;
    }

    const sdkLoaded = await loadRazorpayScript();
    if (!sdkLoaded) {
      toast.error("Failed to load payment SDK.");
      return;
    }

    setLoading(true);
    try {
      const { data: order } = await axios.post(
        paymentURL,
        { country: user.country || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { orderId, amount, currency, key_id } = order;
      const options = {
        key: key_id,
        amount,
        currency,
        name: "SomeOne4u Gold Membership",
        description: `30‑day Gold (${currency} ${(amount / 100).toFixed(2)})`,
        order_id: orderId,
        handler: async (resp) => {
          try {
            const { data: verify } = await axios.post(
              verify_paymentURL,
              {
                orderId,
                paymentId: resp.razorpay_payment_id,
                signature: resp.razorpay_signature,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (verify.membership?.status) {
              toast.success("Gold Membership Activated!");
              navigate("/profile");
            } else {
              toast.error("Activation failed.");
            }
          } catch {
            toast.error("Payment verification failed.");
          }
        },
        prefill: {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          contact: user.mobile,
        },
        theme: { color: "#fbbf24" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create order.");
    } finally {
      setLoading(false);
    }
  };

  const isGold = user?.membership_type === "gold" && user?.membership_status;

  return (
    <>
      <Helmet>
        <title>Gold Membership – SomeOne4u</title>
        <meta
          name="description"
          content="Upgrade to Gold Membership on SomeOne4u to unlock premium features: unlimited messaging, full profile access, priority support, and more."
        />
        <meta
          name="keywords"
          content="SomeOne4u, gold membership, premium dating, upgrade membership"
        />
        <link rel="canonical" href="https://someone4u.com/membership" />

        <meta property="og:title" content="Gold Membership – SomeOne4u" />
        <meta
          property="og:description"
          content="Upgrade to Gold Membership on SomeOne4u to unlock premium features: unlimited messaging, full profile access, priority support, and more."
        />
        <meta property="og:url" content="https://someone4u.com/membership" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SomeOne4u" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-pink-50 to-white">
        <div className="max-w-4xl mx-auto mt-10">
          {isGold && (
            <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 p-4 mb-6 rounded">
              <strong>You're already a Gold Member!</strong>
              {user.membership_expiry && (
                <span>
                  {" "}
                  Valid until{" "}
                  <b>{new Date(user.membership_expiry).toLocaleDateString()}</b>
                </span>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-red-400">
            <h2 className="text-3xl font-bold text-center text-red-500 mb-6">
              Upgrade to Gold Membership
            </h2>

            <div className="text-center mb-8">
              <p className="text-gray-700 text-lg">
                Unlock premium features and supercharge your experience.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Free Plan */}
              <div className="border rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-2">Free</h3>
                <ul className="text-gray-600 space-y-2 mb-4 text-sm">
                  <li>✅ Browse limited profiles</li>
                  {/* <li>✅ Like & match</li> */}
                  <li>✅ Edit profile</li>
                  <li>❌ Full gallery</li>
                  {/* <li>❌ See who viewed you</li> */}
                  <li>❌ Priority in search</li>
                  <li>❌ Unlimited conversations</li>
                </ul>
                <button
                  disabled
                  className="w-full bg-gray-300 text-gray-700 py-2 rounded cursor-not-allowed"
                >
                  Current Plan
                </button>
              </div>

              {/* Gold Plan */}
              <div className="border rounded-lg p-6 shadow-md border-yellow-400">
                <h3 className="text-xl font-semibold text-yellow-600 mb-2">
                  Gold Member
                </h3>
                <ul className="text-gray-700 space-y-2 mb-4 text-sm">
                  <li>✅ Full profiles & galleries</li>
                  <li>✅ Priority support</li>
                  {/* <li>✅ See likes & views</li> */}
                  <li>✅ Top of search</li>
                  <li>✅ Unlimited conversations</li>
                </ul>
                {isGold ? (
                  <button
                    disabled
                    className="w-full bg-yellow-300 text-yellow-800 py-2 rounded cursor-not-allowed"
                  >
                    Already Gold
                  </button>
                ) : (
                  <button
                    onClick={handleUpgrade}
                    disabled={loading}
                    className={`
                      w-full flex items-center justify-center
                      py-2 rounded transition
                      ${loading
                        ? "bg-yellow-400 cursor-not-allowed opacity-70"
                        : "bg-yellow-500 hover:bg-yellow-600 text-white"
                      }`}
                  >
                    {loading && (
                      <span className="animate-spin border-white border-2 border-t-transparent rounded-full w-4 h-4 mr-2"></span>
                    )}
                    {loading ? "Processing..." : "Upgrade to Gold"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Membership;
